import React from 'react'
import * as Yup from 'yup'
import { Button } from '@mui/material'
import { useFormik } from 'formik'
import Field from './Field.tsx'
import ErrorBoundary from '../../misc/ErrorBoundary.jsx'
import { compareAsc, format, isValid } from 'date-fns'
import { context } from '../../index.js'
import { Icons } from '../../misc/Icons.tsx'
import { evaluateDateString } from '../../misc/dateValidation.ts'


export default function FormFields ({
    fields,
    initialValues = {},
    onChange = (formik) => {},
    onSubmit = (values) => {},
    onCancel = null,
    submitCount = 0,
    maxHeight = "none",
    submitButtonText = "Submit",
    quitButtonText = ""
}) {

    var yupObject = {}

    fields.filter(field => !!field.name).forEach(field => {

        // Set intial value to guess
        if (!(field.name in initialValues)) {
            if (field.guess) {
                initialValues[field.name] = field.guess
            } else if (field.type === "checkbox") {
                initialValues[field.name] = "false"
            } else if (field.type === "file") {
                initialValues[field.name] = ""
                initialValues[`${field.name}_not_available`] = "false"
            } else if (field.type === "table") {
                initialValues[field.name] = []
            } else {
                initialValues[field.name] = ""
            }
        }

        // Set validation
        if (field.required !== false && field.type === "table") {
            yupObject[field.name] = Yup.array().required()
            if (typeof field.table?.min_rows === "number") {
                yupObject[field.name] = Yup.array().min(field.table?.min_rows, `You must have at least ${field.table?.min_rows} entries`).required()
            }
            if (typeof field.table?.max_rows === "number") {
                yupObject[field.name] = Yup.array().max(field.table?.min_rows, `You are limited to ${field.table?.min_rows} entries`).required()
            }
        }  else if (field.required !== false && field.type === "date") {

            yupObject[field.name] = Yup.string().test({
                name: field.name,
                // skipAbsent: true,
                test(value, ctx) {

                    var date = new Date(value)

                    if (!isValid(date)) {
                        return ctx.createError({ message: 'Enter a valid date' })
                    }

                    if (field.min) {
                        var minDate = evaluateDateString({...context.answers, ...this.parent}, field.min)
                        if (minDate && compareAsc(date, minDate) < 0) {
                            return ctx.createError({ message: `Enter a date after ${format(minDate, "do LLLL u")}` })
                        }
                    }

                    if (field.max) {
                        var maxDate = evaluateDateString({...context.answers, ...this.parent}, field.max)
                        if (maxDate && compareAsc(date, maxDate) > 0) {
                            return ctx.createError({ message: `Enter a date before ${format(maxDate, "do LLLL u")}` })
                        }
                    }

                    return true
                }
            })
        } else if (field.type === "phone-number" && field.required !== false) {

            var fieldValidation = Yup.string().matches(/^\+[0-9][0-9\- ]{7,20}[0-9]$/g, "Enter a valid phone number")
            yupObject[field.name] = fieldValidation
        
        } else if (field.name && field.required !== false) {

            var regex = field.regex ? field.regex : "^[“”‘’ -~À-Ö\p{Sc}\p{So}\p{Mn}\p{P}\p{Z}À-ÿ\w\n]*$"
            var regexMessage = field.regexMessage ? field.regexMessage : "Only use standard European characters"
            var fieldValidation = Yup.string().matches(regex, regexMessage)

            if (field.min) {
                fieldValidation = fieldValidation.test('min', `You must enter at least ${field.min} characters`, val => val?.length >= field.min)
            }
            if (field.max) {
                fieldValidation = fieldValidation.test('max', `You are limited to ${field.max} characters`, val => val?.length <= field.max)
            }
            if (field.required !== false) {
                fieldValidation = fieldValidation.required("This field is required")
            }

            yupObject[field.name] = fieldValidation
        }
    })

    var formik = useFormik({

        initialValues: initialValues,
        validationSchema: Yup.object(yupObject),
        validateOnMount: true,

        onSubmit: (values) => {
            return onSubmit(values)
        }
    })

    const removeHiddenValues = () => {
        var displayedFieldKeys = []

        fields.forEach(field => {
            if (!context.isHidden(field, formik.values)) {
                displayedFieldKeys.push(field.name)
            }
        })

        fields.forEach(field => {
            if (!displayedFieldKeys.includes(field.name)) {
                delete formik.values[field.name]
                delete formik.errors[field.name]
            }
        })

        return displayedFieldKeys
    }

    React.useEffect(() => {
        removeHiddenValues()
    })

    React.useEffect(() => {
        onChange(formik)
    }, [formik.values, formik.errors])

    return (
        <form className="form" onSubmit={formik.handleSubmit}>
            <div className="form-fields" style={{maxHeight: maxHeight}}>
                {fields.map((field, i) => {

                    var valuesToEvaluateConditions = {}
                    fields.forEach(field => {
                        if (field.name in formik.values && formik.values[field.name] !== "") {
                            valuesToEvaluateConditions[field.name] = formik.values[field.name]
                        } else {
                            valuesToEvaluateConditions[field.name] = undefined
                        }
                    })

                    if (context.isHidden(field, valuesToEvaluateConditions)) {
                        return <React.Fragment key={`${i}-${field.name}`}></React.Fragment>
                    }

                    return (
                        <ErrorBoundary key={`${i}-${field.name}`}>
                            <div>
                                <Field
                                    field={field}
                                    formik={formik}
                                    autoFocus={i === 0} />

                                <span className="warning">
                                    {(submitCount || formik.submitCount) && formik.errors[field.name] ? formik.errors[field.name] : null}
                                </span>
                            </div>
                        </ErrorBoundary>
                    )
                })}
            </div>


            {onCancel || submitButtonText ? (
                <div className="form-buttons" style={{marginTop: "30px"}}>
                    {onCancel ? (
                        <Button
                            onClick={() => onCancel()}
                            className="btn btn-lg"
                            variant="contained"
                            color="info"
                            disabled={formik.isSubmitting || context.locked}>
                            <Icons.ArrowLeft /> Cancel
                        </Button>
                    ) : null}

                    {quitButtonText ? (
                        <Button
                            color="info"
                            variant="contained"
                            className="btn btn-lg"
                            onClick={() => context.saveAndQuit()}>
                            {quitButtonText}
                        </Button>
                    ) : null}

                    {submitButtonText ? (
                        <Button
                            type="submit"
                            color="success"
                            variant="contained"
                            className="btn btn-lg">
                            {submitButtonText}
                        </Button>
                    ) : null}
                </div>
            ) : null}
        </form>
    )
}